import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

/**
 * Common API error response structure
 */
export interface ApiErrorResponse {
    detail?: string;
    code?: string;
    message?: string;
    [key: string]: unknown;
}

/**
 * Special throttle error response structure
 */
export interface ThrottleErrorResponse {
    message: string;
    extra: string;
    type: 'throttleType';
}

/**
 * Common validation error response structure
 */
export interface ValidationErrorResponse {
    [key: string]: string | string[];
}

/**
 * Type guard to check if error data is an ApiErrorResponse
 */
export function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
    return typeof data === 'object' && data !== null;
}

/**
 * Type guard to check if error data is a ThrottleErrorResponse
 */
export function isThrottleErrorResponse(
    data: unknown,
): data is ThrottleErrorResponse {
    return (
        typeof data === 'object' &&
        data !== null &&
        'type' in data &&
        (data as ThrottleErrorResponse).type === 'throttleType'
    );
}

/**
 * Type guard to check if error data is a ValidationErrorResponse
 */
export function isValidationErrorResponse(
    data: unknown,
): data is ValidationErrorResponse {
    return typeof data === 'object' && data !== null && !('type' in data);
}

/**
 * Utility function to check if an error indicates token validation failure
 */
export function isTokenValidationError(error: FetchBaseQueryError): boolean {
    if (!isApiErrorResponse(error.data)) {
        return false;
    }

    const errorData = error.data;

    // Check detail field for token errors
    if (errorData.detail) {
        const detail = errorData.detail.toLowerCase();

        return (
            detail.includes('token not valid') ||
            detail.includes('token_not_valid') ||
            detail.includes('given token not valid')
        );
    }

    // Check code field for token errors
    if (errorData.code && errorData.code === 'token_not_valid') {
        return true;
    }

    // Check message field for token errors
    if (errorData.message && typeof errorData.message === 'string') {
        const message = errorData.message.toLowerCase();

        return (
            message.includes('token not valid') ||
            message.includes('token_not_valid')
        );
    }

    return false;
}

/**
 * Extract error message from API error response
 */
export function extractErrorMessage(error: FetchBaseQueryError): string | null {
    if (!isApiErrorResponse(error.data)) {
        return null;
    }

    const errorData = error.data;

    // Priority: detail > message > code
    if (errorData.detail) {
        return errorData.detail;
    }

    if (errorData.message) {
        return errorData.message;
    }

    if (errorData.code) {
        return errorData.code;
    }

    return null;
}
